import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';

const ShopifyCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [countdown, setCountdown] = useState(3);
  const success = searchParams.get('success') === 'true';
  const shop = searchParams.get('shop'); // Shop domain
  const shopName = searchParams.get('shopName'); // Shop display name
  const error = searchParams.get('error');

  useEffect(() => {
    if (success) {
      // Update localStorage with connection status
      localStorage.setItem('isConnected', 'true');
      localStorage.setItem('platform', 'shopify');
      // Shop parameter is the domain (e.g., my-store.myshopify.com)
      if (shop) {
        let shopUrl = shop;
        // Ensure it's a full URL
        if (!shop.startsWith('http')) {
          shopUrl = `https://${shop}`;
        }
        localStorage.setItem('storeUrl', shopUrl);
      }

      // Countdown and redirect to dashboard
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            navigate('/dashboard', { replace: true });
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [success, shop, navigate]);

  const handleGoToDashboard = () => {
    navigate('/dashboard', { replace: true });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        {success ? (
          <>
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
              <CheckCircleIcon className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Store Connected Successfully!
            </h2>
            {(shopName || shop) && (
              <p className="text-lg text-gray-600 mb-4">
                {shopName || shop}
              </p>
            )}
            <p className="text-sm text-gray-500 mb-6">
              Your Shopify store has been connected to CartResQ. You can now start recovering abandoned carts.
            </p>
            <div className="space-y-3">
              <button
                onClick={handleGoToDashboard}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Go to Dashboard {countdown > 0 && `(${countdown})`}
              </button>
              {countdown > 0 && (
                <p className="text-xs text-gray-400">
                  Redirecting automatically in {countdown} second{countdown !== 1 ? 's' : ''}...
                </p>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
              <XCircleIcon className="h-10 w-10 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Connection Failed
            </h2>
            {error && (
              <p className="text-sm text-red-600 mb-4">
                {decodeURIComponent(error)}
              </p>
            )}
            <p className="text-sm text-gray-500 mb-6">
              We couldn't connect your Shopify store. Please try again or use the manual connection method.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/store-setup', { replace: true })}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate('/store-setup', { replace: true })}
                className="w-full bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Use Manual Connection
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ShopifyCallback;

